import React, { useCallback, useMemo } from "react";
import {
  Button,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@material-ui/core";
import { getFieldProps, inputProps } from "../../utils";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import { useFormikContext } from "formik";
import {
  getCompletionState,
  getNewsFieldByLanguage,
  LANGUAGE_LABELS,
} from "../../shared/languageConfig";
import LanguageSelector from "../../shared/LanguageSelector";
import { usePreferredLanguagePersistence } from "../../shared/usePreferredLanguagePersistence";

const KeyPoints = () => {
  const formik = useFormikContext();
  const validLanguages = formik.values.validLanguages || ["it", "en"];
  const preferredLanguage =
    formik.values.uiPreferredLanguage || validLanguages[0];
  const secondaryLanguages = validLanguages.filter(
    (language) => language !== preferredLanguage
  );
  const activeSecondaryLanguage =
    formik.values.uiActiveSecondaryLanguage || secondaryLanguages[0] || null;
  const preferredFieldName = getNewsFieldByLanguage(preferredLanguage);
  const secondaryFieldName = activeSecondaryLanguage
    ? getNewsFieldByLanguage(activeSecondaryLanguage)
    : null;
  const contentColumnMd = secondaryLanguages.length ? 5 : 11;
  const deleteColumnMd = secondaryLanguages.length ? 2 : 1;
  const persistPreferredLanguage = usePreferredLanguagePersistence();

  const languageCompletionByCode = useMemo(() => {
    return validLanguages.reduce((acc, language) => {
      const fieldName = getNewsFieldByLanguage(language);
      const keyPoints = formik.values?.[fieldName]?.keyPoints || [];
      const totalRequiredFields = keyPoints.length || 1;
      const completed = keyPoints.reduce((total, point) => {
        return total + (point?.trim() ? 1 : 0);
      }, 0);

      acc[language] = getCompletionState({
        completedLanguage: completed,
        isActive: language === activeSecondaryLanguage,
        totalRequiredFields,
      });
      return acc;
    }, {});
  }, [activeSecondaryLanguage, formik.values, validLanguages]);

  const buildTextField = (
    formik,
    placeholder,
    name,
    label,
    required = false,
    md = 6,
    xs = 12
  ) => {
    return (
      <Grid item md={md} xs={xs}>
        <TextField
          fullWidth
          InputLabelProps={{ shrink: true }}
          InputProps={inputProps}
          label={label}
          margin="none"
          placeholder={placeholder}
          required={required}
          variant="outlined"
          {...getFieldProps(formik, {
            name,
          })}
        />
      </Grid>
    );
  };

  const handleAddKeyPoint = useCallback(() => {
    const preferredKeyPoints =
      formik.values[preferredFieldName]?.keyPoints ?? [];
    const updatedPreferredValues = [...preferredKeyPoints, ""];

    formik.setFieldValue(
      `${preferredFieldName}.keyPoints`,
      updatedPreferredValues
    );

    if (secondaryFieldName) {
      const secondaryKeyPoints =
        formik.values[secondaryFieldName]?.keyPoints ?? [];
      const updatedSecondaryValues = [...secondaryKeyPoints, ""];
      formik.setFieldValue(
        `${secondaryFieldName}.keyPoints`,
        updatedSecondaryValues
      );
    }
  }, [formik, preferredFieldName, secondaryFieldName]);

  const handleDeleteKeyPoint = useCallback(
    (i) => () => {
      const newPreferredValue = [
        ...(formik.values?.[preferredFieldName]?.keyPoints || []),
      ];
      newPreferredValue.splice(i, 1);
      formik.setFieldValue(
        `${preferredFieldName}.keyPoints`,
        newPreferredValue
      );

      if (secondaryFieldName) {
        const newSecondaryValue = [
          ...(formik.values?.[secondaryFieldName]?.keyPoints || []),
        ];
        newSecondaryValue.splice(i, 1);
        formik.setFieldValue(
          `${secondaryFieldName}.keyPoints`,
          newSecondaryValue
        );
      }
    },
    [formik, preferredFieldName, secondaryFieldName]
  );
  return (
    <Grid item xs={12}>
      <Grid item>
        <Typography variant="h6">
          We are almost at the end! Summarize the key information of the news!
        </Typography>
      </Grid>

      <LanguageSelector
        languageCompletionByCode={languageCompletionByCode}
        preferredLanguage={preferredLanguage}
        style={{ marginBottom: "1.5rem", marginTop: "1rem" }}
        validLanguages={validLanguages}
        onActiveSecondaryLanguageChange={(language) =>
          formik.setFieldValue("uiActiveSecondaryLanguage", language)
        }
        onPreferredLanguageChange={(nextPreferred, nextSecondary) => {
          formik.setFieldValue("uiPreferredLanguage", nextPreferred);
          formik.setFieldValue("uiActiveSecondaryLanguage", nextSecondary);
          persistPreferredLanguage(nextPreferred);
        }}
      />

      <Grid item xs={12}>
        <>
          <Grid item style={{ marginBottom: "1rem" }}>
            <Typography
              color="textSecondary"
              style={{ fontSize: "1rem" }}
              variant="h6"
            >
              Write down the key points of the news, to summarize the
              information so even those with limited time know what it is about!
            </Typography>
          </Grid>
          {(formik.values?.[preferredFieldName]?.keyPoints || []).map(
            (_, i) => {
              return (
                <React.Fragment key={i}>
                  <Grid
                    container
                    spacing={2}
                    style={{ marginBottom: "1rem", marginTop: "1rem" }}
                  >
                    {buildTextField(
                      formik,
                      "Key point",
                      `${preferredFieldName}.keyPoints[${i}]`,
                      LANGUAGE_LABELS[preferredLanguage] || preferredLanguage,
                      true,
                      contentColumnMd,
                      12
                    )}

                    {secondaryFieldName &&
                      buildTextField(
                        formik,
                        "Key point",
                        `${secondaryFieldName}.keyPoints[${i}]`,
                        LANGUAGE_LABELS[activeSecondaryLanguage] ||
                          activeSecondaryLanguage,
                        false,
                        contentColumnMd,
                        12
                      )}

                    {(formik.values?.[preferredFieldName]?.keyPoints || [])
                      .length > 1 && (
                      <Grid item md={deleteColumnMd} xs={12}>
                        <IconButton onClick={handleDeleteKeyPoint(i)}>
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    )}
                  </Grid>
                </React.Fragment>
              );
            }
          )}
          <Grid
            item
            style={{ marginBottom: "0.5rem", marginTop: "0.5rem" }}
            xs={12}
          >
            <Button
              color="primary"
              disabled={false}
              endIcon={<AddIcon />}
              variant="contained"
              onClick={handleAddKeyPoint}
            >
              Add key point
            </Button>
          </Grid>
        </>
      </Grid>
    </Grid>
  );
};

export default KeyPoints;
